import React, { forwardRef } from 'react'
import { useTranslation } from 'react-i18next'
import { BaseView, BaseText } from '@/components/base'
import { AmountTextInput } from '@/components/AmountTextInput'
import { AppIcons } from '@/components/AppIcons'
import { CryptoSelector } from '../CryptoSelector'
import { useConversionScopedStore } from '@/store/scoped'
import { useAppPreferencesStore } from '@/store/useAppPreferencesStore'
import { useTheme } from '@/hooks/useTheme'
import { currencyUtils } from '@/utils'
import { useCryptoConversion } from '../../hooks'
import { extractFiatSymbol } from '@/utils/currency/extractFiatSymbol'

const { formatDynamicCurrency } = currencyUtils

export interface AnimatedCryptoInputProps {
  type: 'from' | 'to'
  value?: string
  onFocus?: () => void
  isFiduciarySwitchable?: boolean
  onChangeText?: (value: string) => void
  defaultValue: string
  currencyInfoInWallet: any
  equivalentValue: any
  maxLength: number
  containerStyle?: React.CSSProperties
  title?: string
  editable?: boolean
  isLoading?: boolean
}

export const AnimatedCryptoInput = forwardRef<
  HTMLInputElement,
  AnimatedCryptoInputProps
>(
  (
    {
      title,
      type,
      containerStyle,
      isFiduciarySwitchable = false,
      currencyInfoInWallet,
      equivalentValue = { amount: '0', currency: 'USD' },
      value,
      onFocus,
      onChangeText,
      maxLength,
      editable = true,
      isLoading = false,
      ...textInputProps
    },
    ref,
  ) => {
    const { t } = useTranslation()
    const theme = useTheme()
    const isFiatMode = useConversionScopedStore((state) => state.isFiatMode)
    const { handleToggleFiatMode } = useCryptoConversion()
    const displayCurrency = useAppPreferencesStore(
      (state) => state.displayCurrency,
    )

    // Получаем символ валюты для отображения
    const currencySymbol = isFiatMode
      ? extractFiatSymbol({ fiatCode: displayCurrency?.isin || 'USD' })
      : undefined

    const formattedEquivalentValue = formatDynamicCurrency({
      amount: equivalentValue?.amount || '0',
      currencyCode: equivalentValue?.currency || displayCurrency?.isin || 'USD',
      showDecimals: true,
    })

    return (
      <BaseView
        style={{
          position: 'relative',
          backgroundColor: theme.colors.background.textInput,
          borderRadius: 32,
          paddingLeft: 16,
          paddingRight: 8,
          paddingTop: 12,
          paddingBottom: 12,
          minHeight: 90,
          width: '100%',
          flexDirection: 'row',
          alignItems: 'center',
          ...containerStyle,
        }}
      >
        {/* Левая часть - значения */}
        <BaseView
          style={{
            justifyContent: 'space-between',
            flex: 1,
            height: '100%',
          }}
        >
          <BaseText
            fontSize={14}
            fontFamily="semibold"
            color="label.description"
          >
            {title ? t(title) : ''}
          </BaseText>

          {isLoading && (
            <BaseText
              fontSize={28}
              fontFamily="semibold"
              color="label.description"
              style={{
                lineHeight: 1,
                paddingTop: 0,
              }}
            >
              ...
            </BaseText>
          )}

          {!isLoading && (
            <AmountTextInput
              ref={ref}
              value={value}
              onChangeText={onChangeText}
              onFocus={onFocus}
              style={{
                lineHeight: 1,
                fontSize: 28,
                fontWeight: 600,
                color: editable
                  ? theme.colors.label.default
                  : theme.colors.label.description,
                backgroundColor: 'transparent',
                border: 'none',
                outline: 'none',
                padding: 0,
                justifyContent: 'center',
                paddingTop: 0,
                // paddingBottom: 4,
              }}
              placeholder="0"
              currencyCode={
                isFiatMode
                  ? displayCurrency?.isin || 'USD'
                  : currencyInfoInWallet?.code
              }
              shouldMaskStablecoinAsFiat={false}
              prefix={currencySymbol}
              prefixFontSize={22}
              maxLength={maxLength}
              disabled={!editable}
              {...textInputProps}
            />
          )}

          <BaseView pt={4} row align="center">
            <BaseText
              fontSize={12}
              fontFamily="medium"
              color="label.description"
            >
              {formattedEquivalentValue}
            </BaseText>

            {!!isFiduciarySwitchable && (
              <BaseView
                style={{ cursor: 'pointer', padding: 4 }}
                onClick={handleToggleFiatMode}
              >
                <AppIcons name="switch" size={16} color="label.default" />
              </BaseView>
            )}
          </BaseView>
        </BaseView>

        {/* Правая часть - селектор валюты */}
        <CryptoSelector
          type={type}
          currencyInfoInWallet={currencyInfoInWallet}
        />
      </BaseView>
    )
  },
)

AnimatedCryptoInput.displayName = 'AnimatedCryptoInput'
